import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Lock, ArrowRight, ShieldCheck, Users, User, GraduationCap, ChevronRight } from "lucide-react";
import { parentAppAuthService } from "../services/authService";

// Static particle data to prevent re-renders in animations
const SUCCESS_PARTICLES = [...Array(6)].map((_, i) => ({
  x: Math.random() * 60 + 40,
  y: Math.random() * 60 + 40,
  delay: i * 0.1
}));

const WELCOME_PARTICLES = [...Array(8)].map((_, i) => ({
  top: `${Math.random() * 100}%`,
  left: `${Math.random() * 100}%`,
  delay: i * 0.2
}));

export default function LoginPage({ setAuth }) {
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Phone, 2: OTP, 3: Student Selection
  const [phone, setPhone] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtpSuccess, setShowOtpSuccess] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem("parentToken");
    const studentId = localStorage.getItem("selectedStudentId");
    if (token && studentId) {
      setAuth(true);
      navigate("/dashboard");
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000); // simulate loading
  }, [navigate, setAuth]);

  const onSendOtp = async (data) => {
    setIsSubmitting(true);
    try {
      await parentAppAuthService.requestOtp(data.phone);
      setPhone(data.phone);
      setShowOtpSuccess(true);
      // Wait for 2 seconds to show the engaging animation before moving to OTP step
      setTimeout(() => {
        setShowOtpSuccess(false);
        setStep(2);
      }, 2000);
    } catch (err) {
      const message = err.response?.data?.message || "Error sending OTP. Please try again.";
      toast.error(message);
      if (err.response?.status === 403) {
        toast.error("This number is not registered or enrollment is inactive.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onVerifyOtp = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await parentAppAuthService.verifyOtp(phone, data.otp);
      const enrolledStudents = result.data.students;

      localStorage.setItem('parentToken', result.data.token);
      localStorage.setItem('parentPhone', phone); // Save phone number for fetching children later

      if (enrolledStudents && enrolledStudents.length > 0) {
        // Automatically select the first student found
        const firstStudent = enrolledStudents[0];
        const studentId = firstStudent.student._id;

        localStorage.setItem('selectedStudentId', studentId);
        sessionStorage.setItem("student", JSON.stringify(firstStudent.student));

        setSelectedStudentName(firstStudent.student.firstName);
        setShowWelcome(true);
        setAuth(true);

        setTimeout(() => {
          setShowWelcome(false);
          navigate("/dashboard");
        }, 2500);
      } else {
        toast.error("No active students found for this account.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP or selection error.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white md:bg-gradient-to-br md:from-pragyaBlue md:via-pragyaBlue/90 md:to-pragyaBlue relative overflow-hidden font-sans">
      {/* Desktop Background Decorative Elements - Hidden on Mobile */}
      <div className="hidden md:block absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl transform-gpu will-change-transform" />
      <div className="hidden md:block absolute bottom-0 right-0 w-96 h-96 bg-pragyaOrange/20 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl transform-gpu will-change-transform" />


      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-screen relative z-50 transform-gpu">
          <div className="relative">
            <div className="animate-ping absolute inline-flex h-20 w-20 rounded-full bg-pragyaBlue/30 md:bg-white/30 opacity-75 transform-gpu"></div>
            <div className="relative inline-flex rounded-full h-20 w-20 bg-white flex items-center justify-center shadow-lg transform-gpu">
              <img src="/Pragya-Sarathi.png" alt="Logo" className="w-10 h-10 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-pragyaBlue md:text-white font-medium animate-pulse">Initializing Portal...</p>
        </div>
      ) : (
        <div className="flex flex-col md:items-center md:justify-center min-h-screen">

          {/* Mobile Logo Section - Visible on Mobile Only (replaced illustration) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="block md:hidden w-full h-[30vh] relative bg-white flex items-center justify-center p-8"
          >
            <div className="relative">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 bg-pragyaBlue/20 blur-3xl rounded-full overflow-hidden rounded-full"
              />
              <img
                src="/un.png"
                alt="Logo"
                className="w-32 h-32 object-contain relative z-10 drop-shadow-2xl rounded-full"
              />
            </div>
          </motion.div>

          {/* Main Container */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full md:max-w-md flex-1 md:flex-none"
          >
            {/* 
                DESKTOP: Centered card with backdrop blur
                MOBILE: Bottom-sheet style rounded corners, full width 
            */}
            <div className="bg-white md:bg-white/95 md:backdrop-blur-md md:shadow-2xl rounded-t-[2.5rem] md:rounded-3xl p-8 md:p-8 h-full md:h-auto border-t md:border border-slate-100 md:border-white/20 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.1)] md:shadow-2xl">

              {/* Header - DIFFERENT for Mobile and Desktop */}
              <div className="flex flex-col items-center mb-8">
                {/* Desktop Logo Wrapper */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="hidden md:flex w-24 h-24 bg-white rounded-2xl shadow-xl items-center justify-center mb-4 border border-slate-100 transform-gpu"
                >
                  <img src="/Pragya-Sarathi.png" alt="Logo" className="w-20 h-20" />
                </motion.div>

                {/* Mobile Identity */}
                <div className="md:hidden flex items-center justify-center w-full mb-2">
                  <div className="h-1.5 w-12 bg-slate-200 rounded-full mx-auto absolute top-4 left-1/2 -translate-x-1/2" />
                </div>

                <div className="w-full text-left md:text-center mt-2">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight">
                    {step === 3 ? "Select Student" : (step === 1 ? "Welcome Back" : "One Last Step")}
                  </h2>
                  <p className="text-slate-500 mt-1 font-medium text-sm md:text-base">
                    {step === 1 && "Login with your registered mobile number"}
                    {step === 2 && "Enter the 6-digit code sent to your phone"}
                    {step === 3 && "Access your child's information"}
                  </p>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.form
                    key="phone-form"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={handleSubmit(onSendOtp)}
                    className="space-y-6"
                  >
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 ml-1 uppercase tracking-wider">Phone Number</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-slate-400 font-bold border-r border-slate-200 pr-3">+91</span>
                        </div>
                        <input
                          type="tel"
                          {...register("phone", {
                            required: "Phone number is required",
                            pattern: {
                              value: /^[0-9]{10}$/,
                              message: "Invalid phone number (10 digits)"
                            }
                          })}
                          className="w-full pl-16 pr-4 py-4 md:py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-pragyaBlue/10 focus:border-pragyaBlue transition-all text-lg font-semibold"
                          placeholder="00000 00000"
                          disabled={isSubmitting}
                          autoFocus
                        />
                      </div>
                      {errors.phone && (
                        <p className="text-pragyaOrange text-xs mt-2 ml-1 font-medium italic">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-pragyaBlue text-white py-4 md:py-4 rounded-2xl font-bold shadow-lg shadow-pragyaBlue/20 hover:shadow-pragyaBlue/40 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-70"
                    >
                      <span>{isSubmitting ? "Sending OTP..." : "Continue"}</span>
                      {!isSubmitting && <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />}
                    </button>

                    <p className="text-center text-xs text-slate-400 px-4">
                      By continuing, you agree to receive an OTP via SMS for verification.
                    </p>
                  </motion.form>
                )}

                {step === 2 && (
                  <motion.form
                    key="otp-form"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onSubmit={handleSubmit(onVerifyOtp)}
                    className="space-y-6"
                  >
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl mb-6 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold tracking-tight">Code sent to</p>
                        <p className="text-sm font-bold text-slate-800">+91 {phone}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setStep(1); reset(); }}
                        className="text-xs text-pragyaBlue font-bold px-3 py-1.5 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50 transition-colors"
                      >
                        Change
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2 ml-1 uppercase tracking-wider">One Time Password</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock size={18} className="text-slate-400 group-focus-within:text-pragyaBlue transition-colors" />
                        </div>
                        <input
                          type="text"
                          inputMode="numeric"
                          {...register("otp", {
                            required: "OTP is required",
                            minLength: { value: 6, message: "OTP must be 6 digits" }
                          })}
                          className="w-full pl-11 pr-4 py-4 md:py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-pragyaBlue/10 focus:border-pragyaBlue transition-all tracking-[0.6em] font-extrabold text-xl"
                          placeholder="••••••"
                          maxLength={6}
                          disabled={isSubmitting}
                          autoFocus
                        />
                      </div>
                      {errors.otp && (
                        <p className="text-pragyaOrange text-xs mt-2 ml-1 font-medium italic">
                          {errors.otp.message}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-pragyaOrange text-white py-4 rounded-2xl font-bold shadow-lg shadow-pragyaOrange/20 hover:shadow-pragyaOrange/40 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-70"
                      >
                        <ShieldCheck size={20} />
                        <span>{isSubmitting ? "Verifying..." : "Verify & Login"}</span>
                      </button>

                      <button
                        type="button"
                        className="text-sm text-slate-500 font-semibold hover:text-pragyaBlue transition-colors py-2"
                        onClick={() => toast.success("OTP Resent!")}
                      >
                        Didn't receive code? Resend
                      </button>
                    </div>
                  </motion.form>
                )}


              </AnimatePresence>

              {/* Footer Links - Mobile Optimized */}
              <div className="mt-auto md:mt-10 pt-6 border-t border-slate-50 text-center space-y-4">
                <div className="flex items-center justify-center gap-4 text-xs font-bold text-slate-400 uppercase tracking-widest">
                  <span className="cursor-pointer hover:text-pragyaBlue">Support</span>
                  <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
                  <span className="cursor-pointer hover:text-pragyaBlue">Guide</span>
                  <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
                  <span className="cursor-pointer hover:text-pragyaBlue">Terms</span>
                </div>

                <div className="flex items-center justify-center gap-2 text-[10px] text-slate-300 font-medium">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  <span>End-to-end encrypted secure access</span>
                </div>
              </div>

            </div>
          </motion.div>
        </div>
      )}

      {/* Styles for custom scrollbar */}
      <style jsx="true text">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #0098b3;
          border-radius: 10px;
        }
      `}</style>

      {/* Engaging OTP Success Overlay */}
      <AnimatePresence>
        {showOtpSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-pragyaBlue/20 backdrop-blur-sm md:backdrop-blur-md transform-gpu"
          >
            <motion.div
              initial={{ scale: 0.8, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 1.1, opacity: 0 }}
              className="bg-white rounded-[2.5rem] p-10 shadow-2xl max-w-xs w-full text-center relative overflow-hidden"
            >
              {/* Decorative Circle */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-emerald-50 rounded-full -translate-y-1/2" />

              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-200"
                >
                  <ShieldCheck size={40} className="text-white" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-2xl font-extrabold text-slate-800 mb-2"
                >
                  OTP Sent!
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-slate-500 font-medium"
                >
                  Verification code has been dispatched to
                  <span className="block text-pragyaBlue font-bold mt-1">+91 {phone}</span>
                </motion.p>

                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.5, ease: "linear", delay: 0.5 }}
                  className="h-1 bg-emerald-500/20 rounded-full mt-8 overflow-hidden"
                >
                  <motion.div className="h-full bg-emerald-500 w-full" />
                </motion.div>
              </div>

              {/* Success Particles - Decorative */}
              {SUCCESS_PARTICLES.map((particle, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, x: 0, y: 0 }}
                  animate={{
                    scale: [0, 1, 0],
                    x: (i % 2 === 0 ? 1 : -1) * particle.x,
                    y: (i < 3 ? 1 : -1) * particle.y
                  }}
                  transition={{ duration: 1, delay: 0.3 + particle.delay, repeat: Infinity }}
                  className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-emerald-400' : 'bg-pragyaBlue/40'} transform-gpu`}
                  style={{ top: '40%', left: '46%' }}
                />
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Premium Welcome Success Overlay */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-pragyaBlue/40 backdrop-blur-md md:backdrop-blur-lg transform-gpu"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0, rotate: -5 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 1.5, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-white rounded-[3rem] p-12 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] max-w-sm w-full text-center relative overflow-hidden border border-white/20"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pragyaBlue via-pragyaOrange to-pragyaBlue" />

              <div className="relative z-10">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-6 relative inline-block"
                >
                  <div className="w-24 h-24 bg-pragyaBlue/10 rounded-3xl flex items-center justify-center mx-auto transform rotate-12">
                    <img src="/Pragya-Sarathi.png" alt="Logo" className="w-16 h-16 transform -rotate-12" />
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-pragyaBlue/20 blur-2xl rounded-full transform-gpu"
                  />
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-3xl font-black text-slate-800 mb-2 tracking-tight"
                >
                  Welcome Back!
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="text-pragyaBlue text-xl font-bold mb-8"
                >
                  Future Leader...
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 }}
                  className="bg-slate-50 py-3 px-6 rounded-2xl flex items-center justify-center gap-3 text-slate-500 font-semibold"
                >
                  <div className="flex space-x-1">
                    <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ repeat: Infinity, duration: 0.6 }} className="w-1.5 h-1.5 bg-emerald-500 rounded-full transform-gpu" />
                    <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-1.5 h-1.5 bg-emerald-500 rounded-full transform-gpu" />
                    <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }} className="w-1.5 h-1.5 bg-emerald-500 rounded-full transform-gpu" />
                  </div>
                  <span>Securely Logging In</span>
                </motion.div>
              </div>

              {/* Sparkle effects */}
              {WELCOME_PARTICLES.map((particle, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
                  transition={{ duration: 2, delay: particle.delay, repeat: Infinity }}
                  className="absolute transform-gpu"
                  style={{
                    top: particle.top,
                    left: particle.left,
                  }}
                >
                  <div className="w-1 h-1 bg-pragyaOrange rounded-full blur-[1px]" />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


